import { schedulingEntryBoutique } from "@/lib/schedulingEntry";
import useFocusState from "@/hooks/useFocusState";
import SchedulingVessel from "@/components/toto/SchedulingVessel";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import {
  ArrowLeft, Plus, Users, Sparkles, Send, CheckCircle2, XCircle, Trash2,
  Info, HandHeart,
} from "lucide-react";

/**
 * Coverage (manager) / "Help a teammate" (employee) — same data, two framings.
 *
 * Managers see the operational list ("2 open shifts, 1 awaiting assignment").
 * Employees see the invitation ("Help a teammate — Heather needs Friday").
 */

const STATUS_STYLES = {
  open:               { dot: "bg-amber-500",   label: "Open" },
  volunteer_received: { dot: "bg-indigo-500",  label: "Volunteers waiting" },
  assigned:           { dot: "bg-emerald-500", label: "Assigned" },
  zenoti_pending:     { dot: "bg-indigo-500",  label: "Zenoti pending" },
  completed:          { dot: "bg-emerald-500", label: "Completed" },
  cancelled:          { dot: "bg-stone-400",   label: "Cancelled" },
};

const AUDIENCE_LABELS = {
  boutique:    "Entire boutique",
  morning_egs: "Morning EGs",
  mts:         "Massage Therapists",
  egs:         "Experience Guides",
  leadership:  "Leadership only",
};

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hh = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.open;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] schedule-muted bg-stone-50 px-2.5 py-0.5 rounded-full" data-testid={`coverage-status-${status}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function NewCoverageForm({ boutique, mode, onCreated, defaultOrigin }) {
  const [origin, setOrigin] = useState(defaultOrigin || "management_need");
  const [audience, setAudience] = useState("boutique");
  const [date, setDate] = useState("");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!date) { setErr("Pick a date."); return; }
    setSaving(true); setErr("");
    try {
      const r = await api.post("/scheduling/coverage", {
        boutique_code: boutique, origin, audience, date, start, end,
        reason_category: reason, notes,
      });
      onCreated(r.data);
      setDate(""); setNotes(""); setReason("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not post.");
    } finally { setSaving(false); }
  };

  const isEmployee = mode === "employee";

  return (
    <div className="schedule-panel schedule-border rounded-3xl p-6 " data-testid="coverage-new-form">
      <h2 className="font-serif-display schedule-ink text-xl">
        {isEmployee ? "Ask a teammate to cover a shift" : "Post a coverage need"}
      </h2>
      <p className="text-xs schedule-muted mt-1">
        {isEmployee
          ? "Teammates can volunteer; your manager makes the final assignment."
          : "Set the audience, and eligible teammates will see the invitation on their personal home."}
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        {!isEmployee && (
          <div>
            <div className="text-[10px] label-eyebrow schedule-muted mb-1">Audience</div>
            <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="coverage-audience"
              className="w-full px-3 py-1.5 rounded-lg schedule-border text-sm outline-none schedule-panel">
              {Object.entries(AUDIENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        )}
        <div>
          <div className="text-[10px] label-eyebrow schedule-muted mb-1">Date</div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="coverage-date"
            className="w-full px-3 py-1.5 rounded-lg schedule-border text-sm outline-none" />
        </div>
        <div>
          <div className="text-[10px] label-eyebrow schedule-muted mb-1">Start</div>
          <input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="coverage-start"
            className="w-full px-3 py-1.5 rounded-lg schedule-border text-sm outline-none" />
        </div>
        <div>
          <div className="text-[10px] label-eyebrow schedule-muted mb-1">End</div>
          <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="coverage-end"
            className="w-full px-3 py-1.5 rounded-lg schedule-border text-sm outline-none" />
        </div>
        <div className="sm:col-span-4">
          <div className="text-[10px] label-eyebrow schedule-muted mb-1">Reason (optional)</div>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="coverage-reason"
            placeholder={isEmployee ? "personal, medical…" : "extra coverage, private event…"}
            className="w-full px-3 py-1.5 rounded-lg schedule-border text-sm outline-none" />
        </div>
        <div className="sm:col-span-4">
          <div className="text-[10px] label-eyebrow schedule-muted mb-1">Notes (optional)</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="coverage-notes"
            className="w-full px-3 py-1.5 rounded-lg schedule-border text-sm outline-none" />
        </div>
      </div>
      {err && <div className="mt-3 text-sm text-rose-600" data-testid="coverage-error">{err}</div>}
      <div className="mt-4 flex justify-end">
        <button onClick={submit} disabled={saving} data-testid="coverage-submit"
          className="text-sm px-5 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 flex items-center gap-2">
          <Send size={14} strokeWidth={1.5} /> {saving ? "Posting…" : (isEmployee ? "Ask the team" : "Post coverage")}
        </button>
      </div>
    </div>
  );
}

function CoverageDetail({ opp, mode, refresh }) {
  const c = opp.coverage || {};
  const active = (opp.volunteers || []).filter((v) => !v.withdrawn_at);
  const [assigning, setAssigning] = useState(null); // volunteer_id being assigned
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const volunteer = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/scheduling/coverage/${opp.id}/volunteer`, {}); await refresh(); }
    catch (e) { setErr(e.response?.data?.detail || "Could not volunteer."); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    setBusy(true); setErr("");
    try { await api.delete(`/scheduling/coverage/${opp.id}/volunteer`); await refresh(); }
    catch (e) { setErr(e.response?.data?.detail || "Could not withdraw."); }
    finally { setBusy(false); }
  };
  const assign = async (volunteer_id) => {
    setBusy(true); setErr(""); setAssigning(volunteer_id);
    try { await api.post(`/scheduling/coverage/${opp.id}/assign`, { volunteer_id }); await refresh(); }
    catch (e) { setErr(e.response?.data?.detail || "Could not assign."); }
    finally { setBusy(false); setAssigning(null); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this coverage post?")) return;
    setBusy(true);
    try { await api.post(`/scheduling/coverage/${opp.id}/cancel`, {}); await refresh(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`coverage-detail-${opp.id}`}>
      <div className="schedule-panel schedule-border rounded-3xl p-6 ">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] label-eyebrow schedule-muted">
              {opp.origin === "management_need" ? "Manager posted" : "Employee requested"} · {AUDIENCE_LABELS[opp.audience]}
            </div>
            <h2 className="font-serif-display schedule-ink text-2xl mt-1">{c.date}</h2>
            <div className="text-sm schedule-ink mt-0.5">{fmtTime(c.start)} – {fmtTime(c.end)}</div>
            {c.requester_employee_name && (
              <div className="text-xs schedule-muted mt-1">For {c.requester_employee_name}</div>
            )}
            {c.notes && <div className="text-sm schedule-muted mt-2">{c.notes}</div>}
          </div>
          <StatusPill status={opp.status} />
        </div>
      </div>

      {/* Volunteers */}
      <div className="schedule-panel schedule-border rounded-3xl p-6 " data-testid="coverage-volunteers">
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif-display schedule-ink text-lg">Volunteers</h3>
          <span className="text-[11px] schedule-muted">{active.length} {active.length === 1 ? "person" : "people"}</span>
        </div>
        {active.length === 0 ? (
          <div className="text-sm schedule-muted mt-3" data-testid="coverage-no-volunteers">No one has volunteered yet.</div>
        ) : (
          <ul className="mt-3 divide-y divide-stone-100">
            {active.map((v) => (
              <li key={v.employee_id} className="py-3 flex items-center gap-3" data-testid={`coverage-volunteer-${v.employee_id}`}>
                <Avatar name={v.employee_name} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm schedule-ink">{v.employee_name}</div>
                  {v.note && <div className="text-[11px] schedule-muted">"{v.note}"</div>}
                  <div className="text-[10px] schedule-muted mt-0.5">
                    volunteered {new Date(v.volunteered_at).toLocaleString()}
                  </div>
                </div>
                {opp.status === "assigned" && opp.assigned_employee_id === v.employee_id && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Assigned</span>
                )}
                {opp.can_assign && (
                  <button onClick={() => assign(v.employee_id)} disabled={busy}
                    data-testid={`coverage-assign-${v.employee_id}`}
                    className="text-[11px] px-3 py-1 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">
                    {assigning === v.employee_id ? "…" : "Assign"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {opp.status === "assigned" && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-900" data-testid="coverage-assigned-banner">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} strokeWidth={1.5} />
            <span>Assigned to <strong>{opp.assigned_employee_name}</strong>. A Zenoti update task has been queued.</span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {opp.can_volunteer && !opp.already_volunteered && (
          <button onClick={volunteer} disabled={busy} data-testid="coverage-volunteer-btn"
            className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 flex items-center gap-2">
            <HandHeart size={14} strokeWidth={1.5} /> {mode === "employee" ? "I can help" : "Volunteer"}
          </button>
        )}
        {!opp.can_volunteer && !opp.already_volunteered && !opp.is_requester && !opp.can_assign && opp.status !== "assigned" && opp.status !== "cancelled" && opp.status !== "completed" && (
          <div className="text-[11px] schedule-muted flex items-center gap-1.5 bg-stone-50 px-3 py-1.5 rounded-full" data-testid="coverage-leadership-caption">
            <Info size={12} strokeWidth={1.5} /> Leadership view · Volunteering is available to scheduled team members
          </div>
        )}
        {opp.already_volunteered && opp.status !== "assigned" && (
          <button onClick={withdraw} disabled={busy} data-testid="coverage-withdraw-btn"
            className="text-sm px-4 py-2 rounded-full schedule-border schedule-ink hover:bg-stone-50 flex items-center gap-2">
            Withdraw offer
          </button>
        )}
        {opp.can_cancel && (
          <button onClick={cancel} disabled={busy} data-testid="coverage-cancel-btn"
            className="text-sm schedule-muted hover:text-rose-600 flex items-center gap-1.5 ml-auto">
            <Trash2 size={12} strokeWidth={1.5} /> Cancel post
          </button>
        )}
      </div>

      {err && <div className="text-sm text-rose-600" data-testid="coverage-detail-error">{err}</div>}
    </div>
  );
}

function CoverageListItem({ opp, active, onOpen, mode }) {
  const c = opp.coverage || {};
  const summary = mode === "employee" && c.requester_employee_name
    ? `${c.requester_employee_name} · ${c.date}`
    : `${c.date} · ${fmtTime(c.start)}–${fmtTime(c.end)}`;
  const vols = (opp.volunteers || []).filter((v) => !v.withdrawn_at).length;
  return (
    <button
      onClick={() => onOpen(opp.id)}
      data-testid={`coverage-item-${opp.id}`}
      aria-pressed={active}
      className={`schedule-list-row w-full text-left p-4 flex items-center gap-3 transition-colors ${active ? "bg-stone-50" : "hover:bg-stone-50"}`}
    >
      <div className="w-10 h-10 rounded-2xl bg-stone-50 schedule-border flex items-center justify-center schedule-ink">
        {opp.origin === "employee_request" ? <HandHeart size={16} strokeWidth={1.5} /> : <Sparkles size={16} strokeWidth={1.5} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm schedule-ink truncate">{summary}</div>
        <div className="text-[11px] schedule-muted truncate">
          {AUDIENCE_LABELS[opp.audience]}
          {vols > 0 && ` · ${vols} volunteer${vols !== 1 ? "s" : ""}`}
        </div>
      </div>
      <StatusPill status={opp.status} />
    </button>
  );
}

export default function Coverage({ mode = "manager" }) {
  // mode: "manager" (default) or "employee" (rendered at /help-a-teammate)
  const { user } = useAuth();
  const [entryParams] = useSearchParams();
  const entryBoutique = schedulingEntryBoutique(user, entryParams.get("boutique"));
  const { oppId } = useParams();
  const navigate = useNavigate();
  const [boutique, setBoutique] = useState(entryBoutique);
  const [tab, setTab] = useFocusState(mode === "employee" ? "available" : entryParams.get("view") === "all" ? "all" : "open");
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(oppId || null);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const isLeadership = user && !user.boutique_code;
  const boutiques = ["PLT", "FLL", "WYN", "PNC"];

  useEffect(() => setSelectedId(oppId || null), [oppId]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      if (mode === "employee") {
        if (tab === "available") params.audience_for_me = true;
        else if (tab === "mine")  params.mine = true;
        else                      params.audience_for_me = true;
      } else {
        params.boutique = boutique;
        if (tab === "open")     params.status = "volunteer_received";
        else if (tab === "unfilled") params.status = "open";
      }
      const r = await api.get("/scheduling/coverage", { params });
      let list = r.data?.opportunities || [];
      if (mode === "manager" && tab === "all") {
        // Show non-cancelled non-completed
        list = list.filter((o) => !["cancelled", "completed"].includes(o.status));
      }
      setItems(list);
    } finally { setLoading(false); }
  }, [boutique, mode, tab]);
  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    api.get(`/scheduling/coverage/${selectedId}`).then((r) => setSelected(r.data)).catch(() => setSelected(null));
  }, [selectedId]);

  const refresh = async () => {
    await loadList();
    if (selectedId) {
      const r = await api.get(`/scheduling/coverage/${selectedId}`);
      setSelected(r.data);
    }
  };

  const title = mode === "employee" ? "Help a teammate" : "Coverage";
  const description = mode === "employee"
    ? "Shifts your teammates need help with. Your manager makes the final call — you're invited, not obligated."
    : "Open coverage needs and volunteer offers waiting for your assignment.";
  const managerTabs = [
    { id: "open", label: "Volunteers waiting" },
    { id: "unfilled", label: "Unfilled" },
    { id: "all", label: "All active" },
  ];
  const employeeTabs = [
    { id: "available", label: "Available" },
    { id: "mine", label: "My posts" },
  ];
  const tabs = mode === "employee" ? employeeTabs : managerTabs;

  return (
    <SchedulingVessel title={title} description={description} backTo={mode === "employee" ? "/people" : "/scheduling"} backLabel={mode === "employee" ? "Team" : "Scheduling"}>
        <div className="flex items-baseline justify-between flex-wrap gap-3 mt-2">

          <div className="flex items-center gap-2">
            {isLeadership && mode === "manager" && (
              <select value={boutique} onChange={(e) => setBoutique(e.target.value)} data-testid="coverage-boutique"
                className="px-4 py-2 rounded-full schedule-border text-sm schedule-panel outline-none">
                {boutiques.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            <button onClick={() => setShowForm((v) => !v)} aria-expanded={showForm} data-testid="coverage-new-toggle"
              className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 flex items-center gap-2">
              <Plus size={14} strokeWidth={1.5} /> {mode === "employee" ? "Ask for coverage" : "Post coverage"}
            </button>
          </div>
        </div>

        {showForm && (
          <div className="mt-6">
            <NewCoverageForm
              boutique={boutique} mode={mode}
              defaultOrigin={mode === "employee" ? "employee_request" : "management_need"}
              onCreated={() => { setShowForm(false); refresh(); }}
            />
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-3">
            <div className="sonmi-nav sonmi-nav--primary flex items-center gap-1" data-testid="coverage-tabs">
              {tabs.map((t) => (
                <button key={t.id} aria-pressed={tab === t.id} onClick={() => setTab(t.id)} data-testid={`coverage-tab-${t.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full ${tab === t.id ? "bg-stone-900 text-white" : "schedule-panel schedule-border schedule-muted hover:bg-stone-50"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="schedule-panel schedule-border rounded-2xl divide-y divide-stone-100 overflow-hidden" data-testid="coverage-list">
              {loading ? (
                <div className="p-6 text-sm schedule-muted">Loading…</div>
              ) : items.length === 0 ? (
                <div className="p-6 text-sm schedule-muted" data-testid="coverage-list-empty">
                  {mode === "employee" ? "No coverage requests are open right now — a calm day." : "Nothing in this bucket yet."}
                </div>
              ) : items.map((o) => (
                <CoverageListItem key={o.id} opp={o} active={selectedId === o.id} onOpen={(id) => { setSelectedId(id); navigate(mode === "employee" ? `/help-a-teammate/${id}` : `/scheduling/coverage/${id}`); }} mode={mode} />
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            {selected ? (
              <CoverageDetail opp={selected} mode={mode} refresh={refresh} />
            ) : (
              <div className="schedule-panel schedule-border rounded-3xl p-10 text-sm schedule-muted text-center">
                {mode === "employee"
                  ? "Pick a request on the left to see when your teammate needs help."
                  : "Pick a post on the left to see volunteers and assign."}
              </div>
            )}
          </div>
        </div>
    </SchedulingVessel>
  );
}
